import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, faUpload, faDownload, faSpinner, faFileExcel,
  faCheckCircle, faExclamationTriangle, faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import * as ExcelJS from 'exceljs';
import { brandService } from '../../services/brandService';
import { BrandImportData } from '../../types/brand';

interface BrandImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete?: (count: number) => void;
}

const BrandImportModal: React.FC<BrandImportModalProps> = ({
  isOpen,
  onClose,
  onImportComplete
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [previewData, setPreviewData] = useState<BrandImportData[]>([]);

  const generateTemplate = async () => {
    const template = [
      {
        'Value': '1883',
        '#BrandLogo': '',
        'WEDLEigenmarkeFlag': ''
      },
      {
        'Value': 'Poggio al Sole Winery', 
        '#BrandLogo': '\\Binaries\\64a2d1de-40db-42e8-8cbe-3663d7b3f3b2.jpg',
        'WEDLEigenmarkeFlag': ''
      },
      {
        'Value': '11er',
        '#BrandLogo': '',
        'WEDLEigenmarkeFlag': 'true'
      }
    ];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Marken');
    
    // Add headers
    const headers = Object.keys(template[0]);
    worksheet.addRow(headers);
    
    // Add data rows
    template.forEach(row => {
      worksheet.addRow(Object.values(row));
    });
    
    // Download the file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'marken_template.xlsx';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setImportResult(null);
      parseExcelFile(selectedFile);
    }
  };

  const parseExcelFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const buffer = e.target?.result as ArrayBuffer;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);
        const worksheet = workbook.worksheets[0];
        
        // Convert worksheet to JSON data
        const jsonData: any[] = [];
        const headers: string[] = [];
        
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) {
            // Header row
            row.eachCell((cell, colNumber) => {
              headers[colNumber - 1] = cell.value?.toString() || `Column${colNumber}`;
            });
          } else {
            // Data rows
            const record: any = {};
            row.eachCell((cell, colNumber) => {
              const header = headers[colNumber - 1];
              if (header) {
                record[header] = cell.value;
              }
            });
            if (Object.keys(record).length > 0) {
              jsonData.push(record);
            }
          }
        });

        console.log('[BrandImport] Parsed Excel data:', jsonData);

        const brands: BrandImportData[] = jsonData.map((row: any) => {
          console.log('[BrandImport] Processing row:', row);
          return {
            value: (row['_Value'] || row['Value'] || row['value'] || row['Marke'] || '').toString().trim(),
            brandLogo: (row['#BrandLogo'] || row['_#BrandLogo'] || row['brandLogo'] || row['Logo'] || '').toString().trim(),
            wedlEigenmarkeFlag: row['WEDLEigenmarkeFlag'] === 'true' || 
                               row['WEDLEigenmarkeFlag'] === true || 
                               row['_WEDLEigenmarkeFlag'] === 'true' ||
                               row['_WEDLEigenmarkeFlag'] === true ||
                               row['wedlEigenmarkeFlag'] === 'true' ||
                               row['wedlEigenmarkeFlag'] === true ||
                               row['Eigenmarke'] === 'true' ||
                               row['Eigenmarke'] === true
          };
        }).filter(brand => brand.value !== '');

        console.log('[BrandImport] Filtered brands:', brands);
        setPreviewData(brands);
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        alert('Fehler beim Lesen der Excel-Datei. Bitte stellen Sie sicher, dass es sich um eine gültige Excel-Datei handelt.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    if (!previewData.length) {
      alert('Keine Daten zum Importieren gefunden.');
      return;
    }

    setImporting(true);
    try {
      const result = await brandService.importBrands(previewData);
      setImportResult(result);
      onImportComplete?.(result.successful || result.count || 0);
    } catch (error) {
      console.error('Import error:', error);
      alert('Fehler beim Importieren der Marken. Bitte versuchen Sie es erneut.');
    } finally {
      setImporting(false);
    }
  };

  const resetModal = () => {
    setFile(null);
    setImporting(false);
    setImportResult(null);
    setPreviewData([]);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FontAwesomeIcon icon={faFileExcel} className="text-purple-600 text-xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Marken Import</h2>
                <p className="text-gray-600">Excel-Datei mit Marken importieren</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xl text-gray-500" />
            </button>
          </div>

          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {!importResult ? (
              <>
                {/* Template Download */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600" />
                    <h3 className="font-semibold text-blue-900">Excel-Vorlage</h3>
                  </div>
                  <p className="text-blue-800 mb-3">
                    Laden Sie die Excel-Vorlage herunter und füllen Sie diese mit Ihren Marken-Daten aus.
                  </p>
                  <button
                    onClick={generateTemplate}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                    Vorlage herunterladen
                  </button>
                </div>

                {/* File Upload */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Excel-Datei auswählen
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      className="hidden"
                      id="excel-upload"
                    />
                    <label
                      htmlFor="excel-upload"
                      className="cursor-pointer flex flex-col items-center gap-3"
                    >
                      <FontAwesomeIcon icon={faUpload} className="text-3xl text-gray-400" />
                      <div>
                        <p className="text-lg font-medium text-gray-600">
                          {file ? file.name : 'Excel-Datei auswählen'}
                        </p>
                        <p className="text-sm text-gray-500">
                          Unterstützte Formate: .xlsx, .xls
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Preview */}
                {previewData.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Vorschau ({previewData.length} Marken)
                    </h3>
                    <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium text-gray-700">Marke</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-700">Logo</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-700">Eigenmarke</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.slice(0, 10).map((brand, index) => (
                            <tr key={index} className="border-t border-gray-200">
                              <td className="px-4 py-2">{brand.value}</td>
                              <td className="px-4 py-2 text-gray-500 truncate max-w-32">
                                {brand.brandLogo || '-'}
                              </td>
                              <td className="px-4 py-2">
                                {brand.wedlEigenmarkeFlag ? (
                                  <span className="text-green-600">✓</span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {previewData.length > 10 && (
                        <div className="p-3 text-center text-gray-500 text-sm border-t">
                          ... und {previewData.length - 10} weitere Marken
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Import Results */
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon 
                    icon={faCheckCircle} 
                    className="text-2xl text-green-600" 
                  />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Import abgeschlossen</h3>
                    <p className="text-gray-600">{importResult.message}</p>
                  </div>
                </div>

                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <h4 className="font-medium text-yellow-800 mb-2">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                      Fehler beim Import
                    </h4>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {importResult.errors.map((error: any, index: number) => (
                        <p key={index} className="text-sm text-yellow-700">
                          Zeile {error.row}: {error.name} - {error.error}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
            <button
              onClick={handleClose}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              {importResult ? 'Schließen' : 'Abbrechen'}
            </button>
            {/* Debug Info */}
            <div className="text-xs text-gray-500 mr-auto">
              Debug: {previewData.length} Marken, ImportResult: {importResult ? 'ja' : 'nein'}
            </div>
            {!importResult && previewData.length > 0 && (
              <button
                onClick={handleImport}
                disabled={importing}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {importing ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    Importiere...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faUpload} />
                    Importieren ({previewData.length} Marken)
                  </>
                )}
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default BrandImportModal;
